/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable no-unsafe-optional-chaining */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Avatar, Box, Button, Typography, useTheme } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import uniqolor from 'uniqolor';
import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import Timeline, {
  DateHeader,
  SidebarHeader,
  TimelineHeaders
} from 'react-calendar-timeline';
import moment from 'moment';
import api from '../../api';
import Loader from '../../components/shared/Loader';
import {
  mainColors,
  secondaryColors,
  successColors
} from '../../theme/themeConfig';
import AssignmentsTask from './AssignmentsTask';
import ConfirmModal from '../../components/modals/ConfirmModal';
import useNavigationStore from '../../store/zustand/navigation';
import useNotificationsStore from '../../store/zustand/notifications';

const keys = {
  groupIdKey: 'id',
  groupLabelKey: 'title',
  groupRightTitleKey: 'rightTitle',
  groupTitleKey: 'title',
  itemDivTitleKey: 'title',
  itemGroupKey: 'group',
  itemIdKey: 'id',
  itemTimeEndKey: 'end_time',
  itemTimeStartKey: 'start_time',
  itemTitleKey: 'title'
};

interface Props {
  destinationId: number;
  id: string;
  projectEndDate: string;
  projectStartDate: string;
  projectStatus: string;
  refresh: () => void;
}

const CheckedIcon = <CheckIcon fontSize="small" />;

const Assignments: React.FC<Props> = function Assignments({
  destinationId,
  id,
  projectEndDate,
  projectStartDate,
  projectStatus,
  refresh
}) {
  const { t } = useTranslation();
  const { addNotification } = useNotificationsStore((state) => state);
  const { sidebarOpen } = useNavigationStore((state) => state);
  const [getAssignments, setGetAssignments] = useState<boolean>(false);
  const [groups, setGroups] = useState<any[]>([]);
  const [items, setItems] = useState<any[]>([]);
  const [openDetail, setOpenDetail] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fireDelete, setFireDelete] = useState<boolean>(false);
  const [auxVisibleTimeEnd, setAuxVisibleTimeEnd] = useState<any>(null);
  const [auxVisibleTimeStart, setAuxVisibleTimeStart] = useState<any>(null);
  const [width, setWidth] = useState<any>(
    window.innerWidth - (sidebarOpen ? 0 : 186)
  );
  const theme = useTheme();

  const auxStart = moment(projectStartDate);
  const auxEnd = moment(projectEndDate);

  const defaultTimeStart =
    auxStart.toDate() < new Date()
      ? moment(projectStartDate).subtract(1, 'd').toDate()
      : moment().subtract(2, 'd').toDate();

  const defaultTimeEnd = moment(defaultTimeStart).add(30, 'days').toDate();

  const minTime = auxStart.add(-1, 'day').valueOf();

  const maxTime =
    auxEnd.diff(auxStart, 'days') < 30 && auxEnd.toDate()
      ? moment(defaultTimeStart).add(30, 'days').valueOf()
      : auxEnd.add(1, 'day').valueOf();

  const {
    data: assignmentsData,
    isValidating: isValidatingAssignments,
    mutate: mutateAssignments
  } = useSWR(
    getAssignments ? { destinationId, id, url: `/assignments/${id}` } : null,
    api.ProjectApi.getAssigments
  );

  const {
    data: unassignmentData,
    error: unassignmentDataError,
    isValidating: isValidatingUnassignment
  } = useSWR(
    fireDelete && items.filter((item: any) => item.selected).length > 0
      ? {
          assignmentsIdsList: items.filter((item: any) => item.selected),
          destinationId,
          projectId: id
        }
      : null,
    api.ProjectApi.unassignResources
  );

  const selectGroup = (group: any) => {
    const selectStatus = !group?.selected;

    const auxIndex = groups.findIndex(
      (groupData: any) => groupData.id === group.id
    );
    const auxGroups = groups;
    auxGroups[auxIndex].selected = !auxGroups[auxIndex].selected;

    setGroups(
      groups.map((groupData: any) => {
        return {
          ...groupData,
          selected: groupData.id === group.id ? selectStatus : false
        };
      })
    );

    let selectedCounter = 0;

    setItems(
      items.map((itemData: any) => {
        if (
          (!itemData?.status || itemData.status !== 'Completed') &&
          itemData.group === group.id &&
          moment(itemData.end_time).toDate() > moment().toDate() &&
          (group.level === 3 || group.level === 2)
        ) {
          // limit the number of selected items to 30
          if (selectStatus) {
            selectedCounter += 1;
          }
          return {
            ...itemData,
            selected: !!selectStatus
          };
        }

        return {
          ...itemData,
          selected: false
        };
      })
    );
  };

  const itemSelect = (item: any) => {
    const auxDate = items.find((dateInfo: any) => dateInfo.id === item.id);
    // limit the number of selected items to 30
    if (
      !auxDate?.selected &&
      items.filter((dateInfo: any) => dateInfo.selected).length > 29
    ) {
      addNotification(t('NOTIFICATION.TOAST.maxSelected', [30]), 'info');
      return;
    }
    const fiteredItems = items.filter(
      (dateInfo: any) => dateInfo.id !== item.id
    );

    if (
      items.filter((dateInfo: any) => dateInfo.selected).length > 0 &&
      items
        .filter((dateInfo: any) => dateInfo.selected)
        .every(
          (dateInfo: any) =>
            dateInfo.taskId === item.taskId &&
            ((item.technicianId &&
              dateInfo.technicianId &&
              item.technicianId === dateInfo.technicianId) ||
              (!item.technicianId && !dateInfo.technicianId))
        )
    ) {
      setItems([...fiteredItems, { ...auxDate, selected: !auxDate.selected }]);
    } else {
      setItems([
        ...fiteredItems.map((dateInfo: any) => {
          return { ...dateInfo, selected: false };
        }),
        { ...auxDate, selected: !auxDate.selected }
      ]);
    }
  };

  const itemRenderer = ({ item, getItemProps }: any) => {
    let disableClick = false;

    if (
      moment(item.end_time).toDate() < moment().toDate() ||
      (item?.status &&
        (item.status === 'Completed' || item.status === 'Started'))
    ) {
      disableClick = true;
    }

    // Determine bar color
    let barColor = '#BDBDBD'; // grey for past/disabled
    if (!disableClick) {
      if (item?.selected) {
        barColor = mainColors.MAIN; // primary blue when selected
      } else if (!item?.assignmentId) {
        // Unassigned slot — color by date urgency matching the status banners
        const daysFromNow = moment(item.start_time).diff(moment(), 'days');
        if (daysFromNow <= 3) {
          barColor = '#C62828'; // red — matches banner title color
        } else if (daysFromNow <= 10) {
          barColor = '#FFE082'; // amber — matches banner title color
        } else {
          barColor = '#66BB6A'; // green — matches banner title color
        }
      } else {
        // Assigned → unique color per technician
        barColor = uniqolor(item.technicianId || item.group || '').color;
      }
    }

    const auxProps = getItemProps({
      style: {
        borderRightWidth: 0,
        borderWidth: 0,
        cursor: disableClick ? 'not-allowed' : 'pointer',
        overflow: 'hidden'
      }
    });

    return (
      <Box
        {...auxProps}
        className="item-box-container-assignments double-item-assignments"
        onClick={() => {
          return disableClick ? null : itemSelect(item);
        }}
        style={{
          ...auxProps.style,
          background: barColor,
          backgroundColor: barColor,
          border: 'none',
          borderRadius: '6px',
          boxShadow: item?.selected
            ? `0 0 0 2px ${mainColors.MAIN}, 0 0 0 4px rgba(0,93,162,0.2)`
            : 'none',
          filter: disableClick ? 'brightness(0.7)' : 'brightness(1)',
          overflow: 'hidden'
        }}
        title={item.title}
      >
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            height: '100%',
            overflow: 'hidden',
            px: '8px'
          }}
        >
          {item?.selected && (
            <Typography
              sx={{
                color: '#fff',
                fontSize: '11px',
                fontWeight: 700,
                textShadow: '0 1px 2px rgba(0,0,0,0.2)'
              }}
            >
              ✓
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  const generateNestedView = useCallback(
    (group: any) => {
      let backGroundColor = `${secondaryColors.SECONDARY_6} !important`;
      if (group.level === 0) {
        return (
          <Box
            className="group-service-time-assignments"
            style={{
              background: backGroundColor,
              backgroundColor: backGroundColor,
              display: 'flex',
              justifyContent: 'space-between'
            }}
          >
            <Box sx={{ display: 'block' }}>
              <Box sx={{ display: 'flex', ml: '0.5rem', mt: '0.5rem' }}>
                <Typography
                  fontFamily={theme.typography.primaryBase.font}
                  variant="body_200"
                >
                  {group.title}
                </Typography>
                {/*
                <Box sx={{ display: 'flex', ml: '0.5rem' }}>
                  <Box sx={{ display: 'flex', height: '25px' }}>
                    <i className="custom-icon icon-clock" />
                  </Box>
                  <Typography sx={{ ml: '0.5rem' }} variant="body_200">
                    {group?.startTime || ''} - 05:00 PM
                  </Typography>
                </Box> */}
              </Box>
            </Box>
          </Box>
        );
      }
      let auxPaddingLeft = 1;

      if (group.level === 2) {
        auxPaddingLeft = 2;
        backGroundColor = `${mainColors.MAIN_1} !important`;
      }

      if (group.level === 3) {
        auxPaddingLeft = 2;
        backGroundColor = `white !important`;
      }

      return (
        <Box
          className={group.level !== 0 ? 'group-service-assignments' : ''}
          onClick={() => {
            return group.level === 2 || group.level === 3
              ? selectGroup(group)
              : null;
          }}
          sx={{
            alignItems: 'center',
            backgroundColor: backGroundColor,
            borderBottom: '1px solid #F0F1F3',
            cursor: 'pointer',
            display: 'flex',
            height: '52px',
            justifyContent: 'space-between',
            paddingLeft: group.level === 3 ? 1 : auxPaddingLeft,
            paddingRight: '0.5rem'
          }}
          title={`${group.title} Rate: $${group?.rate}`}
        >
          {group.level === 3 && group?.initials ? (
            // Technician row: avatar + name + role
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                gap: '8px',
                overflow: 'hidden'
              }}
            >
              <Avatar
                alt={group.title}
                src=""
                sx={{
                  backgroundColor: uniqolor(group?.technicianId || '').color,
                  flexShrink: 0,
                  fontSize: '11px',
                  fontWeight: 700,
                  height: 30,
                  width: 30
                }}
              >
                <Typography color="white" fontSize="11px" fontWeight={700}>
                  {group?.initials}
                </Typography>
              </Avatar>
              <Box sx={{ overflow: 'hidden' }}>
                <Typography
                  sx={{
                    color: '#0f1c2e',
                    fontSize: '12px',
                    fontWeight: 600,
                    lineHeight: 1.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {group.title}
                </Typography>
                <Typography
                  sx={{
                    color: group.isPartnerProxy ? mainColors.MAIN : '#7D8090',
                    fontSize: '10px',
                    fontWeight: group.isPartnerProxy ? 600 : 400
                  }}
                >
                  {group.isPartnerProxy ? 'Partner' : 'Technician'}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Typography
              fontFamily={theme.typography.primaryBase.font}
              sx={{ color: '#0f1c2e', fontSize: '12px', fontWeight: 600 }}
            >
              {group.title}
            </Typography>
          )}
          {group.level === 2 && (
            <Typography
              fontFamily={theme.typography.primaryBase.font}
              variant="body_200"
            >
              {group?.details}
            </Typography>
          )}
        </Box>
      );
    },
    [groups]
  );

  useEffect(() => {
    if (assignmentsData) {
      setGetAssignments(false);
      const { groups: newGroups, items: newItems } = assignmentsData;
      setGroups(newGroups);
      setItems(newItems);
      setIsModalOpen(false);

      if (auxVisibleTimeStart && auxVisibleTimeEnd) {
        // setDefaultTimeStart(auxVisibleTimeStart);
        // setDefaultTimeEnd(auxVisibleTimeEnd);
      }
    }
  }, [assignmentsData]);

  useEffect(() => {
    setLoading(true);
    if (sidebarOpen) {
      setWidth(width - 186);
    } else {
      setWidth(width + 186);
    }
  }, [sidebarOpen]);

  useEffect(() => {
    if (
      unassignmentData &&
      !unassignmentDataError &&
      !isValidatingUnassignment
    ) {
      setFireDelete(false);
      const { groups: newGroups, items: newItems } = unassignmentData;
      setGroups(newGroups);
      setItems(newItems);

      if (auxVisibleTimeStart && auxVisibleTimeEnd) {
        // setDefaultTimeStart(auxVisibleTimeStart);
        // setDefaultTimeEnd(auxVisibleTimeEnd);
      }
      mutateAssignments(unassignmentData);
      setIsModalOpen(false);
    }
  }, [unassignmentData]);

  useEffect(() => {
    setGetAssignments(true);
    const handleResize = () => {
      setLoading(true);
      setWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleTimeChange = (
    visibleTimeStart: any,
    visibleTimeEnd: any,
    updateScrollCanvas: any
  ) => {
    setAuxVisibleTimeStart(visibleTimeStart);
    setAuxVisibleTimeEnd(visibleTimeEnd);

    if (visibleTimeStart < minTime && visibleTimeEnd > maxTime) {
      updateScrollCanvas(minTime, maxTime);
    } else if (visibleTimeStart < minTime) {
      updateScrollCanvas(
        minTime,
        minTime + (visibleTimeEnd - visibleTimeStart)
      );
    } else if (visibleTimeEnd > maxTime) {
      updateScrollCanvas(
        maxTime - (visibleTimeEnd - visibleTimeStart),
        maxTime
      );
    } else {
      updateScrollCanvas(visibleTimeStart, visibleTimeEnd);
    }
  };

  const transformedGroups = useMemo(() => {
    if (!groups) {
      return [];
    }
    return groups.map((group) => {
      return { ...group, title: generateNestedView(group) };
    });
  }, [groups, items]);

  useEffect(() => {
    if (width) {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  }, [width]);

  // ── Status summary banners ───────────────────────────────────────────────
  const statusBanners = useMemo(() => {
    const now = moment();
    // Task-level rows (no technicianId) represent each scheduled day slot.
    // currentAssignments vs requiredAssignments tells us if the day is covered.
    const taskSlots = items.filter(
      (item: any) =>
        !item?.technicianId &&
        item?.taskId &&
        moment(item.end_time).toDate() >= now.toDate()
    );
    const open3 = taskSlots.filter(
      (item: any) =>
        (item.currentAssignments ?? 0) < (item.requiredAssignments ?? 1) &&
        moment(item.start_time).diff(now, 'days') >= 0 &&
        moment(item.start_time).diff(now, 'days') <= 3
    );
    const open10 = taskSlots.filter(
      (item: any) =>
        (item.currentAssignments ?? 0) < (item.requiredAssignments ?? 1) &&
        moment(item.start_time).diff(now, 'days') > 3 &&
        moment(item.start_time).diff(now, 'days') <= 10
    );
    const covered = taskSlots.filter(
      (item: any) =>
        (item.currentAssignments ?? 0) >= (item.requiredAssignments ?? 1) &&
        moment(item.start_time).diff(now, 'days') > 10
    );

    return {
      covered: covered.length,
      open3: open3.length,
      open10: open10.length
    };
  }, [items]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      sx={{ height: 'auto' }}
      width="100%"
    >
      {/* ── Status summary banners ── */}
      {items.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          {/* Red: urgent open */}
          <Box
            sx={{
              backgroundColor: '#FFF0F0',
              border: '1px solid #FFCDD2',
              borderRadius: '10px',
              flex: 1,
              px: 2,
              py: '12px'
            }}
          >
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                gap: '6px',
                mb: '4px'
              }}
            >
              <Typography sx={{ color: '#C62828', fontSize: '11px' }}>
                ⚠
              </Typography>
              <Typography
                sx={{ color: '#C62828', fontSize: '13px', fontWeight: 700 }}
              >
                {statusBanners.open3} open — next 3 days
              </Typography>
            </Box>
            <Typography sx={{ color: '#E57373', fontSize: '11px' }}>
              {statusBanners.open3 > 0
                ? 'Upcoming dates need coverage'
                : 'All upcoming dates covered'}
            </Typography>
          </Box>

          {/* Yellow: medium-term open */}
          <Box
            sx={{
              backgroundColor: '#FFFDE7',
              border: '1px solid #FFE082',
              borderRadius: '10px',
              flex: 1,
              px: 2,
              py: '12px'
            }}
          >
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                gap: '6px',
                mb: '4px'
              }}
            >
              <Typography
                sx={{ color: '#E65100', fontSize: '11px', fontWeight: 700 }}
              >
                !
              </Typography>
              <Typography
                sx={{ color: '#E65100', fontSize: '13px', fontWeight: 700 }}
              >
                {statusBanners.open10} open — next 10 days
              </Typography>
            </Box>
            <Typography sx={{ color: '#FFA726', fontSize: '11px' }}>
              {statusBanners.open10 > 0
                ? 'Partial coverage in this range'
                : 'Range fully staffed'}
            </Typography>
          </Box>

          {/* Green: covered */}
          <Box
            sx={{
              backgroundColor: '#F1F8E9',
              border: '1px solid #C5E1A5',
              borderRadius: '10px',
              flex: 1,
              px: 2,
              py: '12px'
            }}
          >
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                gap: '6px',
                mb: '4px'
              }}
            >
              <Typography sx={{ color: '#2E7D32', fontSize: '11px' }}>
                ✓
              </Typography>
              <Typography
                sx={{ color: '#2E7D32', fontSize: '13px', fontWeight: 700 }}
              >
                {statusBanners.covered} dates covered — 10+ days
              </Typography>
            </Box>
            <Typography sx={{ color: '#66BB6A', fontSize: '11px' }}>
              {statusBanners.covered > 0
                ? 'Fully staffed beyond 10 days'
                : 'No assignments beyond 10 days'}
            </Typography>
          </Box>
        </Box>
      )}

      {/* ── Action toolbar (compact, context-sensitive) ── */}
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, mb: 2 }}>
        <Button
          color="primary"
          disabled={
            items.filter(
              (dateInfo: any) => dateInfo.selected && !dateInfo?.technicianId
            ).length === 0 ||
            projectStatus === 'Completed' ||
            projectStatus === 'Cancelled'
          }
          onClick={() => setOpenDetail(true)}
          size="small"
          sx={{
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: 600,
            px: '14px',
            textTransform: 'none'
          }}
          variant="contained"
        >
          {t('GENERAL.assign')}
        </Button>
        <Button
          color="success"
          disabled={
            items.filter(
              (dateInfo: any) => dateInfo.selected && dateInfo?.technicianId
            ).length === 0 ||
            projectStatus === 'Completed' ||
            projectStatus === 'Cancelled'
          }
          onClick={() => setOpenDetail(true)}
          size="small"
          sx={{
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: 600,
            px: '14px',
            textTransform: 'none'
          }}
          variant="outlined"
        >
          {t('GENERAL.reassign')}
        </Button>
        <Button
          color="error"
          disabled={
            items.filter(
              (dateInfo: any) => dateInfo.selected && dateInfo?.technicianId
            ).length === 0 ||
            projectStatus === 'Completed' ||
            projectStatus === 'Cancelled'
          }
          onClick={() => setIsModalOpen(true)}
          size="small"
          sx={{
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: 600,
            px: '14px',
            textTransform: 'none'
          }}
          variant="outlined"
        >
          {t('GENERAL.unassign')}
        </Button>
      </Box>

      {isValidatingAssignments ||
        isValidatingUnassignment ||
        (loading && (
          <Box marginBottom={3} marginTop={2}>
            <Loader customColor="primary" />
          </Box>
        ))}

      {transformedGroups && items && items.length > 0 && (
        <Box sx={{ overflow: 'scroll' }}>
          {!loading && (
            <Box sx={{}}>
              <Timeline
                itemTouchSendsClick
                stackItems
                traditionalZoom
                buffer={1}
                className="calendar-header-blue-open"
                defaultTimeEnd={defaultTimeEnd}
                defaultTimeStart={defaultTimeStart}
                groups={transformedGroups}
                itemHeightRatio={0.7}
                itemRenderer={itemRenderer}
                items={items}
                keys={keys}
                lineHeight={52}
                maxZoom={30 * 86400 * 1000}
                minZoom={30 * 86400 * 1000}
                onTimeChange={handleTimeChange}
                scrollRef={() => {
                  return null;
                }}
                sidebarWidth={250}
                timeSteps={{
                  day: 1,
                  hour: 1,
                  minute: 1,
                  month: 1,
                  second: 1,
                  year: 1
                }}
              >
                <TimelineHeaders
                  style={{
                    backgroundColor: '#F5F6F8',
                    borderBottom: '1px solid #E2E4EA'
                  }}
                >
                  <SidebarHeader>
                    {({ getRootProps }) => {
                      return (
                        <Box
                          {...getRootProps()}
                          sx={{
                            backgroundColor: '#F5F6F8',
                            borderRight: '1px solid #E2E4EA'
                          }}
                        >
                          <Box
                            sx={{
                              alignItems: 'center',
                              display: 'flex',
                              height: '100%',
                              px: 2
                            }}
                          >
                            <Typography
                              sx={{
                                color: '#7D8090',
                                fontSize: '10px',
                                fontWeight: 700,
                                letterSpacing: '0.06em'
                              }}
                            >
                              TECHNICIAN
                            </Typography>
                          </Box>
                        </Box>
                      );
                    }}
                  </SidebarHeader>
                  <DateHeader
                    className="primaryHeader12"
                    height={30}
                    unit="primaryHeader"
                  />
                  <DateHeader className="primaryHeader22" />
                </TimelineHeaders>
              </Timeline>
            </Box>
          )}
        </Box>
      )}
      {openDetail && (
        <AssignmentsTask
          datesList={items.filter((item: any) => item.selected) || []}
          destinationId={destinationId}
          refresh={(assignmentData: { groups: any[]; items: any[] }) => {
            mutateAssignments(assignmentData);
          }}
          setGetAssignments={setGetAssignments}
          setOpenDetail={setOpenDetail}
          setParentGroups={setGroups}
          setParentItems={setItems}
          taskData={{
            ...items.filter((item: any) => item.selected)[0],
            projectId: id
          }}
        />
      )}
      {isModalOpen && (
        <ConfirmModal
          modalText={t('MODAL.confirmModalUnassign')}
          modalType="status"
          setSentFlag={setFireDelete}
          setShowModal={setIsModalOpen}
          setShowOkModal={() => {
            return false;
          }}
        />
      )}
    </Box>
  );
};

export default Assignments;
